import { useState, useEffect } from 'react';

const UPDATE_DISMISSED_KEY = 'pwa_update_dismissed';
const UPDATE_PROCESSED_KEY = 'pwa_update_processed';
const UPDATE_PROCESSING_KEY = 'pwa_update_processing';

export function usePWAUpdate() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);

  useEffect(() => {
    // Solo en producción
    if (import.meta.env.DEV) {
      return;
    }

    // Verificar si acabamos de recargar tras procesar una actualización
    const isProcessing = sessionStorage.getItem(UPDATE_PROCESSING_KEY);
    if (isProcessing) {
      // Limpiar la marca de procesamiento para estar listos para futuros updates
      sessionStorage.removeItem(UPDATE_PROCESSING_KEY);
      // Salir temprano para evitar falsos positivos de controllerchange o loops al recargar
      return;
    }

    // Verificar si ya procesamos esta actualización recientemente (último minuto)
    const lastProcessed = localStorage.getItem(UPDATE_PROCESSED_KEY);
    if (lastProcessed) {
      const timeSinceProcessed = Date.now() - parseInt(lastProcessed);
      if (timeSinceProcessed < 60000) { // 1 minuto
        return;
      }
    }

    // Registrar el Service Worker y listeners
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setOfflineReady(true);

        // Escuchar cuando se encuentra una nueva actualización y cambia de estado
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // Nueva versión disponible: mostrar aviso al usuario en lugar de recargar automáticamente
                  setNeedRefresh(true);
                  setShowUpdatePrompt(true);
                }
              }
            });
          }
        });

        // Verificar actualizaciones cada hora
        setInterval(() => {
          reg.update().catch(err => console.error('[SW Update Error]:', err));
        }, 60 * 60 * 1000); // 1 hora
      });

      // Escuchar cambios en el Service Worker (nueva versión tomó control)
      let controllerChangeHandled = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (controllerChangeHandled) return;

        // Solo recargar de forma automática si el usuario inició la actualización explícitamente
        if (sessionStorage.getItem(UPDATE_PROCESSING_KEY) === 'true') {
          controllerChangeHandled = true;
          sessionStorage.removeItem(UPDATE_PROCESSING_KEY);
          localStorage.setItem(UPDATE_PROCESSED_KEY, Date.now().toString());
          window.location.reload();
        }
      });

      // Detectar cuando hay un nuevo SW esperando al cargar la página (solo si la página está controlada)
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (navigator.serviceWorker.controller && reg?.waiting) {
          // Si hay una actualización esperando, mostrar aviso para que el usuario decida actualizar
          setNeedRefresh(true);
          setShowUpdatePrompt(true);
        }
      });
    }
  }, []);

  const closePrompt = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
    setShowUpdatePrompt(false);
  };

  const updateApp = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg?.waiting) {
        // Marcar que estamos procesando para que al recargar sepamos qué hacer
        sessionStorage.setItem(UPDATE_PROCESSING_KEY, 'true');
        
        // Enviar mensaje al SW para que se active
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      } else {
        // Fallback si por alguna razón no hay waiting worker
        window.location.reload();
      }
    } catch (error) {
      console.error('[PWA Update Trigger Error]:', error);
      window.location.reload();
    }
    setShowUpdatePrompt(false);
  };

  return {
    offlineReady,
    needRefresh,
    showUpdatePrompt,
    closePrompt,
    updateApp,
  };
}
